import type { OrderDetail } from '@/lib/api-admin/types';
import { formatOrderItemOption, type OrderItemOptionSource } from '@/features/orders/lib/orderItemOption';

/**
 * QA-625 환불 구분 — 결제 관리 환불·수기 환불에서 고른다(BE payment #30·order #143).
 * - NON_PRODUCT(상품 외: 배송비·차액·보상 등): 주문·상품 상태 변경 없음.
 * - PRODUCT(상품 환불): 상품·수량을 함께 보내고, 상품별 상태에 환불완료·부분환불로 남는다. 재고·마일리지·쿠폰은 되돌리지 않는다.
 * 클레임에서 연 환불은 claimId 만 보낸다(구분과 함께 보내면 BE 400).
 */
export type RefundScope = 'PRODUCT' | 'NON_PRODUCT';

export interface RefundScopeItem {
  orderItemId: string;
  productName: string;
  optionLabel: string | null;
  /** 주문 수량 − 취소·반품 − 이미 상품 환불한 수량. BE 검증과 같은 값이라 선택 한도로 쓴다. */
  refundableQuantity: number;
  /** 청구통화 major 단가 문자열(라인 결제 단가). 없으면 기본 금액을 채우지 않는다. */
  chargeUnitAmount: string | null;
  chargeCurrencyCode: string | null;
}

export interface RefundScopeState {
  scope: RefundScope | null;
  /** orderItemId → 환불 수량(0 = 고르지 않음). */
  quantities: Record<string, number>;
}

export const EMPTY_REFUND_SCOPE: RefundScopeState = { scope: null, quantities: {} };

/** 관리자 주문 상세 items[] 의 QA-625 확장 필드 — generated 타입 반영 전이라 안전 캐스팅으로 읽는다. */
interface OrderItemRefundExtra {
  refundableQuantity?: number | null;
  chargeUnitAmount?: string | null;
  chargeCurrencyCode?: string | null;
}

/** 환불할 수 있는 상품(남은 수량 1개 이상). BE 미반영(필드 없음)이면 빈 목록 — 상품 환불을 고를 수 없다. */
export const refundScopeItemsOf = (order: OrderDetail | null | undefined): RefundScopeItem[] =>
  (order?.items ?? []).flatMap((item) => {
    const extra = item as unknown as OrderItemRefundExtra;
    const refundable = extra.refundableQuantity ?? 0;
    if (refundable <= 0) return [];
    return [
      {
        orderItemId: item.orderItemId,
        productName: item.productName ?? '',
        optionLabel: formatOrderItemOption(item as unknown as OrderItemOptionSource),
        refundableQuantity: refundable,
        chargeUnitAmount: extra.chargeUnitAmount ?? null,
        chargeCurrencyCode: extra.chargeCurrencyCode ?? null,
      },
    ];
  });

const chosen = (state: RefundScopeState) =>
  Object.entries(state.quantities).filter(([, q]) => q > 0);

/**
 * 상품 환불 기본 금액(청구통화 major) — Σ chargeUnitAmount × 수량, 통화 소수 자릿수로 반올림.
 * 고른 상품이 없거나, 단가가 없거나, 결제 통화와 다르면 null(운영자가 직접 입력).
 */
export const productRefundDefaultAmount = (
  items: ReadonlyArray<RefundScopeItem>,
  state: RefundScopeState,
  currencyCode: string,
  decimals: number,
): number | null => {
  const picks = chosen(state);
  if (picks.length === 0) return null;
  const scale = 10 ** decimals;
  let totalScaled = 0;
  for (const [id, qty] of picks) {
    const item = items.find((i) => i.orderItemId === id);
    if (!item || item.chargeUnitAmount == null || item.chargeCurrencyCode !== currencyCode) return null;
    const unit = Number(item.chargeUnitAmount);
    if (!Number.isFinite(unit)) return null;
    totalScaled += Math.round(unit * scale) * qty;
  }
  return totalScaled / scale;
};

/** 보내기 전 확인 — 구분을 안 골랐거나, 상품 환불인데 상품을 안 골랐으면 메시지 키. */
export const refundScopeError = (state: RefundScopeState): 'scopeRequired' | 'itemsRequired' | null => {
  if (state.scope == null) return 'scopeRequired';
  if (state.scope === 'PRODUCT' && chosen(state).length === 0) return 'itemsRequired';
  return null;
};

/** 요청 본문 조각 — 상품 외는 items 없이(BE: NON_PRODUCT 에 items 를 보내면 400). */
export const refundScopeBody = (
  state: RefundScopeState,
): { refundScope: RefundScope; items?: Array<{ orderItemId: string; quantity: number }> } =>
  state.scope === 'PRODUCT'
    ? { refundScope: 'PRODUCT', items: chosen(state).map(([orderItemId, quantity]) => ({ orderItemId, quantity })) }
    : { refundScope: 'NON_PRODUCT' };
